import { CONTENT_TYPE_HEADER, HTTP_HEADERS } from '../../auth/const';
import isNative from '../native/isNative';
import { getDomain } from '../native/localStorage';
import { addTenantPrefix } from '../tenantBasePath';
import { tenantURIToPath } from '../tenantURIToPath';
import { getOriginFromReq } from './getOriginFromReq';
import type { FetchArgs, FetchOptions } from './types';

export const isServer = () => typeof window === 'undefined';

export function getRefreshUrl(baseURL: string) {
  return `${baseURL}/auth/refresh-token`;
}

export async function getResponseData(response: Response, options: FetchOptions | undefined) {
  // if response is event stream, return the response as is
  if (
    options?.returnRawResponse ||
    response.headers.get(CONTENT_TYPE_HEADER)?.includes('text/event-stream')
  ) {
    return response;
  }

  const responseData = await response.text();
  try {
    return JSON.parse(responseData) as object;
  } catch (e) {
    return responseData;
  }
}

export async function prependBaseURL(url: string, options?: FetchOptions) {
  if (isServer()) {
    const { headers: nextHeaders } = await import('next/headers');
    const header = await nextHeaders();
    const tenantUri = header.get(HTTP_HEADERS.TenantURI);
    return tenantUri ? addTenantPrefix(url, tenantURIToPath(tenantUri)) : url;
  }

  if (options?.meta?.basePath) {
    return addTenantPrefix(url, options.meta.basePath);
  }

  return url;
}

export async function getBaseURL({
  url,
  options,
}: Pick<FetchArgs, 'url'> & { options?: FetchOptions }) {
  const isPartialUrl = url.startsWith('/');

  //if native and url is partial then prepend base domain else return url
  if (isNative()) {
    const _domain = options?.meta?.apiDomain ?? getDomain();
    return isPartialUrl ? `${_domain}${url}` : url;
  }

  let baseURL = ''; // will be empty for client to use relative url
  // for server get host from headers if url is partial
  if (isServer() && isPartialUrl) {
    baseURL = await getOriginFromReq();
  } else if (url.startsWith('http')) {
    // if url is full url then return url
    return url;
  } else if (!isServer() && options?.meta?.apiDomain) {
    // if meta.apiDomain is set then use it
    baseURL = options.meta.apiDomain;
  } else if (!isServer() && '__unifyBaseUrl' in window) {
    // if __unifyBaseUrl is set then use it
    baseURL = window.__unifyBaseUrl as string;
  }

  return prependBaseURL(`${baseURL}${url}`, options);
}

export function getQueryParams(
  { data, params }: Pick<FetchArgs, 'data' | 'params'>,
  options: FetchOptions,
) {
  const debugParams = options.debugParams ?? ({} as Record<string, string>);
  if (data && 'lookupType' in data && 'type' in data && typeof data.type === 'string') {
    debugParams[data.type] = data.lookupType as string;
  }
  let queryParams = new URLSearchParams({
    ...params,
    ...debugParams,
  }).toString();
  queryParams = queryParams ? `?${queryParams}` : '';
  return queryParams;
}
